import React, { useEffect } from 'react';
import parsePhoneNumber, { isValidPhoneNumber } from 'libphonenumber-js';
import clsx from 'clsx';
import ValidationStore, { addValidation, removeValidation } from 'lib/stores/validationStore';
import ConfigStore from 'lib/stores/configStore';
import ValidationTypes from 'lib/constants/validationTypes';
import useFieldValid from 'lib/hooks/useFieldValid';
import useFormField from 'lib/hooks/useFormField';
import { get } from 'lib/request';

const withValidations = ({ type, timeout, name, required: defaultRequired }) => WrappedComponent => props => {
  // eslint-disable-next-line react/destructuring-assignment
  const [valid, error] = useFieldValid(name || props.name, type);
  // eslint-disable-next-line react/destructuring-assignment
  const [value] = useFormField(props.name);
  const { required: propsRequired, ...otherProps } = props;
  const required = typeof propsRequired !== 'undefined' ? propsRequired : defaultRequired;
  const validationClass = !valid ? 'is-invalid' : 'is-valid';
  const inputClass = clsx('form-control', !value ? '' : validationClass);
  const userKind = ConfigStore.useState(s => s.userKind);

  const runValidation = async () => {
    if (!required && value === '') {
      ValidationStore.update(s => removeValidation(s, props.name, type));

      return;
    }

    if (type === ValidationTypes.EMAIL) {
      // eslint-disable-next-line no-useless-escape
      const response = await get('/users/validate_email', { params: { email: value, user_kind: userKind } });

      if (props.skipUnique || props.firebaseEnabled) {
        const validEmail = response.data.valid || (!response.data.valid && response.data.key.includes('existing'));

        ValidationStore.update(s => {
          addValidation(s, props.name, validEmail, 'invalid_email', type);
        });

        return;
      }

      ValidationStore.update(s => {
        addValidation(s, props.name, response.data.valid, response.data.key, type);
      });

      return;
    }

    if (type === ValidationTypes.PASSWORD) {
      const response = await get('/users/validate_password', { params: { password: value } });

      ValidationStore.update(s => {
        addValidation(s, props.name, response.data.valid, 'invalid_password');
      });

      return;
    }

    if (type === ValidationTypes.PHONE) {
      const phoneNumber = parsePhoneNumber(value, 'AU');

      if (!phoneNumber && phoneNumber !== undefined) return;

      const validNumber = isValidPhoneNumber(value, 'AU');

      ValidationStore.update(s => {
        addValidation(s, props.name, validNumber, 'invalid_phone', type);
      });

      if (!validNumber || props.skipVerify) return;

      const response = await get('/users/validate_phone', { params: { phone: value } });

      ValidationStore.update(s => {
        addValidation(s, props.name, response.data.valid, 'existing_phone', type);
      });

      return;
    }

    if (type === ValidationTypes.IMAGE) {
      if (!required && !value.size) {
        ValidationStore.update(s => removeValidation(s, props.name, type));
        return;
      }

      const isValidFormat = value && (value.type === 'image/png' || value.type === 'image/jpeg');

      ValidationStore.update(s => {
        addValidation(s, props.name, isValidFormat, 'incorrect_format', type);
      });

      return;
    }

    if (Array.isArray(value)) {
      ValidationStore.update(s => addValidation(s, props.name, value.length > 0));

      return;
    }

    ValidationStore.update(s => addValidation(s, props.name, value !== ''));
  };

  useEffect(() => {
    if (type === ValidationTypes.CUSTOM) return;
    if (props.skipValidations) return;

    if (timeout && value) {
      const timer = setTimeout(async () => {
        await runValidation();
      }, timeout);

      return () => clearTimeout(timer);
    }

    const runAsyncValidation = async () => {
      await runValidation();
    };

    runAsyncValidation();
  }, [value]);

  return (
    <WrappedComponent
      valid={valid}
      inputClass={inputClass}
      value={value}
      required={required}
      error={error}
      {...otherProps}
    />
  );
};

export default withValidations;
